import { Box } from '@radix-ui/themes';
import { TimeSlider, TimeSliderInstance } from '@vidstack/react';
import { useEffect, useRef, useState } from 'react';
import { Track } from '@features/Clip/types/media';
import { SmartPlayerPreview } from './SmartPlayerPreview';

const useSliderRect = () => {
    const sliderRef = useRef<TimeSliderInstance>(null);
    const [sliderRect, setSliderRect] = useState<DOMRect | undefined>(
        undefined,
    );

    useEffect(() => {
        const element = sliderRef.current?.el;
        if (!element) return;

        const observer = new ResizeObserver(([entry]) => {
            setSliderRect(entry.target.getBoundingClientRect());
        });

        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return { sliderRef, sliderRect };
};

interface SmartPlayerTimelineProps {
    tracks?: Track[];
}

export const SmartPlayerTimeline = ({
    tracks = [],
}: SmartPlayerTimelineProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const { sliderRef, sliderRect } = useSliderRect();

    return (
        <Box
            onPointerEnter={() => setIsHovered(true)}
            onPointerLeave={() => setIsHovered(false)}
            style={{ width: '100%', pointerEvents: 'auto' }}
        >
            <TimeSlider.Root
                ref={sliderRef}
                style={{
                    width: '100%',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                }}
            >
                <TimeSlider.Track
                    style={{
                        width: '100%',
                        height: isHovered ? '6px' : '4px',
                        backgroundColor: 'var(--gray-a6)',
                        borderRadius: '3px',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'height 0.2s ease',
                    }}
                >
                    <TimeSlider.TrackFill
                        style={{
                            backgroundColor: 'var(--accent-9)',
                            width: 'var(--slider-fill)',
                            height: '100%',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            willChange: 'width',
                        }}
                    />
                    <TimeSlider.Progress
                        style={{
                            backgroundColor: 'var(--gray-a4)',
                            width: 'var(--slider-progress)',
                            height: '100%',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            willChange: 'width',
                        }}
                    />
                </TimeSlider.Track>

                <TimeSlider.Thumb
                    style={{
                        position: 'absolute',
                        left: 'var(--slider-fill)',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '12px',
                        height: '12px',
                        backgroundColor: 'var(--gray-12)',
                        borderRadius: '50%',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity 0.2s ease, transform 0.1s ease',
                        willChange: 'left, opacity',
                    }}
                />

                <SmartPlayerPreview
                    tracks={tracks}
                    sliderRect={sliderRect}
                    isHovered={isHovered}
                />
            </TimeSlider.Root>
        </Box>
    );
};
